import { Capacitor } from '@capacitor/core';
import { Directory, Encoding, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { downloadFile } from './browser-utils';

export type SystemShareResult = 'shared' | 'cancelled' | 'unavailable';

// Capacitor Share v8 rejects user cancellation with the literal message "Share canceled"
// on both Android (SharePlugin.java) and iOS (SharePlugin.swift). Web rejects with
// AbortError DOMException.
function isShareCancellation(err: unknown): boolean {
  if (err instanceof DOMException && err.name === 'AbortError') return true;
  if (err instanceof Error && err.message === 'Share canceled') return true;
  return false;
}

/** Tries to present the system share sheet.
 *  - On Capacitor native: uses @capacitor/share (Intent.ACTION_SEND on Android,
 *    UIActivityViewController on iOS).
 *  - On web: uses navigator.share if available (preserves browser-native UX).
 *  Returns 'shared' on success, 'cancelled' if the user dismissed (caller MUST NOT
 *  fall back), 'unavailable' if no share API is reachable or the call failed
 *  (caller should fall back, e.g. clipboard + toast/alert).
 *
 *  `text` is an optional written message to send alongside the link — the words a
 *  person would put in the chat message themselves. Leave it out to share a bare
 *  link, which is what every caller did before this option existed.
 *
 *  **Do not put the link inside `text` as well.** Android's share plugin joins the
 *  two itself: when both are given it sends `text + ' ' + url` as one string
 *  (SharePlugin.java, "If they supplied both fields, concat them"), so a message
 *  that already ends with the link would show that link twice. Write `text` as the
 *  sentence that leads up to the link and let `url` finish it. */
export async function trySystemShare(opts: {
  title: string;
  url: string;
  text?: string;
}): Promise<SystemShareResult> {
  if (Capacitor.isNativePlatform()) {
    try {
      await Share.share(opts);
      return 'shared';
    } catch (err) {
      if (isShareCancellation(err)) return 'cancelled';
      console.log('trySystemShare: native Share.share failed', err);
      return 'unavailable';
    }
  }
  if (!navigator.share) {
    return 'unavailable';
  }
  try {
    await navigator.share(opts);
    return 'shared';
  } catch (err) {
    if (isShareCancellation(err)) return 'cancelled';
    console.log('trySystemShare: navigator.share failed', err);
    return 'unavailable';
  }
}

export enum ExportFileResult {
  /** the file was downloaded (web) or handed to the share sheet and sent somewhere (native) */
  Exported = 'exported',
  /** the user dismissed the share sheet; nothing to report */
  Cancelled = 'cancelled',
}

/** The platform pieces `exportFileAsync` needs. Tests pass fakes; the app uses the real ones. */
export interface ExportFileDeps {
  isNativePlatform(): boolean;
  /** writes UTF-8 text to the app's cache folder and returns the file:// uri it was written to */
  writeCacheFile(filename: string, data: string): Promise<string>;
  shareFiles(opts: { title: string; files: string[] }): Promise<void>;
  browserDownload(data: string, filename: string, mimeType: string): void;
}

const realExportFileDeps: ExportFileDeps = {
  isNativePlatform: () => Capacitor.isNativePlatform(),
  writeCacheFile: async (filename, data) => {
    const result = await Filesystem.writeFile({ path: filename, data, directory: Directory.Cache, encoding: Encoding.UTF8 });
    return result.uri;
  },
  shareFiles: async (opts) => {
    await Share.share(opts);
  },
  browserDownload: downloadFile,
};

/** Hands a text file (a CSV export, a JSON snapshot) to the user.
 *  - Web: the ordinary browser download, as before.
 *  - Capacitor native: the WebView ignores a download link (no download handler is
 *    registered on iOS or Android), so the file is written to the app's cache folder
 *    and offered through the system share sheet, where the user can save it to Files
 *    or Drive, mail it, or open it in another app (#4075). On Android this relies on the
 *    FileProvider paths covering the cache folder; see the note beside the provider in
 *    client/android-overlay/app/src/main/AndroidManifest.xml.
 *  Resolves `Cancelled` when the user dismisses the share sheet. Any other failure
 *  (the file could not be written, the share sheet could not open) is THROWN, so the
 *  caller can tell the user instead of the export silently doing nothing. */
export async function exportFileAsync(
  opts: { data: string; filename: string; mimeType: string },
  deps: ExportFileDeps = realExportFileDeps,
): Promise<ExportFileResult> {
  // if on the web, the browser download works
  if (!deps.isNativePlatform()) {
    deps.browserDownload(opts.data, opts.filename, opts.mimeType);
    return ExportFileResult.Exported;
  }

  // on native, write the file and hand it to the share sheet
  const uri = await deps.writeCacheFile(opts.filename, opts.data);
  try {
    await deps.shareFiles({ title: opts.filename, files: [uri] });
  } catch (err) {
    if (isShareCancellation(err)) return ExportFileResult.Cancelled;
    throw err;
  }
  return ExportFileResult.Exported;
}
